import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

interface UseElementHeightReturn<T extends HTMLElement> {
  ref: RefObject<T | null>;
  height: number;
}

// Standard SSR-safe pattern: useLayoutEffect on the client (synchronous, pre-paint),
// useEffect on the server (no-op, no warning). Lets us measure and commit the height
// before the browser paints the hydrated DOM, so callers don't flicker on first frame.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Hook to measure and track the height of an element dynamically.
 * Uses ResizeObserver to update when element size changes.
 */
export function useElementHeight<T extends HTMLElement = HTMLDivElement>(): UseElementHeightReturn<T> {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number>(0);

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Functional updater short-circuits on unchanged height so ResizeObserver firing
    // on width-only changes doesn't trigger downstream re-renders.
    const update = (next: number) => setHeight((prev) => (prev === next ? prev : next));

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) update(entry.target.clientHeight);
    });

    resizeObserver.observe(element);
    update(element.clientHeight);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return { ref, height };
}
